/**
 * View Spawner Process
 * 
 * Spawns views at regular intervals with varying policies and behaviors.
 * Each view is color-coded and labeled based on its lifecycle, allocation,
 * and behavior preferences.
 * 
 * **Characteristics:**
 * - Priority: NORMAL
 * - Lifecycle: Varies (cycles through policies)
 * - Allocation: Varies (cycles through policies)
 * - Views: Multiple views (one per spawn)
 * - Spawn interval: 1 second
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { roundedRect, DEFAULT_BORDER_RADIUS } from '../../rendering/shapes.js';
import { bus } from '../../core/events.js';
import { state } from '../../core/state.js';

interface SpawnerViewState {
  spawnIndex: number;
  createdAt: number;
  priority?: number;
  allowedPages?: number[];
  showDismissButton: boolean;
  gridSize: { cols: number; rows: number }; // Current grid size (1x1, 1x2, 2x1, 2x2)
}

/**
 * Policy configuration with color coding
 */
interface PolicyConfig {
  priority?: number;
  allowedPages?: number[];
  showDismissButton: boolean;
  backgroundColor: string;
  borderColor: string;
  label: string;
  description: string;
}

/**
 * Policy configurations - cycles through different behaviors
 */
const POLICY_CONFIGS: PolicyConfig[] = [
  {
    priority: 100, // High priority - protected
    allowedPages: [0], // Must stay on main grid
    showDismissButton: false,
    backgroundColor: '#ffebee', // Light red - protected
    borderColor: '#d32f2f', // Red border
    label: 'PROTECTED',
    description: 'High priority, cannot be dismissed'
  },
  {
    priority: 1, // Normal priority
    showDismissButton: true,
    backgroundColor: '#e8f5e9', // Light green - preservable
    borderColor: '#388e3c', // Green border
    label: 'NORMAL',
    description: 'Standard priority'
  },
  {
    priority: 1, // Normal priority
    showDismissButton: true,
    backgroundColor: '#fff3e0', // Light orange - collapsible
    borderColor: '#f57c00', // Orange border
    label: 'COLLAPSIBLE',
    description: 'Can collapse to minimal size'
  },
  {
    priority: 1, // Normal priority
    showDismissButton: true,
    backgroundColor: '#f3e5f5', // Light purple - dismissable
    borderColor: '#7b1fa2', // Purple border
    label: 'DISMISSABLE',
    description: 'Can be fully dismissed'
  },
  {
    priority: 1, // Normal priority
    showDismissButton: true,
    backgroundColor: '#fff9c4', // Light yellow - overlay
    borderColor: '#f9a825', // Yellow border
    label: 'OVERLAY',
    description: 'Floats over content'
  },
  {
    priority: 1, // Normal priority
    showDismissButton: true,
    backgroundColor: '#e0f2f1', // Light teal - spawn new
    borderColor: '#00695c', // Teal border
    label: 'SPAWN_NEW',
    description: 'Always spawns new view'
  }
];

export class SpawnerProcess extends BaseProcess {
  kind: string = 'spawner';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'demo' as const,
    canStop: true,
    preserveState: false,
    timeSensitive: false
  };

  private spawnCounter: number = 0;
  private spawnTimer: number | null = null;
  private mutationTimer: number | null = null;
  private spawnedViews: Map<string, SpawnerView> = new Map();
  private mutateHandler: ((payload: unknown) => void) | null = null;
  private configHandler: ((payload: unknown) => void) | null = null;
  private spawnInterval: number = 1000; // Default 1 second
  private mutationInterval: number = 2500; // Default 2.5 seconds
  private mutationEnabled: boolean = true;

  constructor(id: string) {
    super(id);
  }

  protected onStart(ctx: ProcessContext): void {
    // Spawn first view immediately
    this.spawnView(ctx);

    // Start spawning views
    this.restartSpawnTimer();

    // Start mutation timer
    this.restartMutationTimer();

    // Listen for signals from external AI/orchestrator
    this.mutateHandler = (payload: unknown) => {
      const data = payload as { viewId?: string; action: 'resize' | 'dismiss' | 'random'; gridSize?: { cols: number; rows: number } };
      this.handleMutationSignal(data);
    };
    bus.on('spawner.mutate', this.mutateHandler);

    // Listen for configuration changes
    this.configHandler = (payload: unknown) => {
      const data = payload as { spawnInterval?: number; mutationInterval?: number; mutationEnabled?: boolean };
      this.handleConfigSignal(data);
    };
    bus.on('spawner.config', this.configHandler);

    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  /**
   * Restart spawn timer with current interval
   */
  private restartSpawnTimer(): void {
    if (this.spawnTimer) {
      clearInterval(this.spawnTimer);
      // Note: We can't untrack it, but that's okay - BaseProcess cleanup will handle it
    }
    this.spawnTimer = window.setInterval(() => {
      this.spawnView(this.context!);
    }, this.spawnInterval);
    this.trackInterval(this.spawnTimer);
  }

  /**
   * Restart mutation timer with current interval
   */
  private restartMutationTimer(): void {
    if (this.mutationTimer) {
      clearInterval(this.mutationTimer);
      // Note: We can't untrack it, but that's okay - BaseProcess cleanup will handle it
    }
    if (this.mutationEnabled) {
      this.mutationTimer = window.setInterval(() => {
        this.mutateViews();
      }, this.mutationInterval);
      this.trackInterval(this.mutationTimer);
    }
  }

  /**
   * Handle configuration signals
   */
  private handleConfigSignal(data: { spawnInterval?: number; mutationInterval?: number; mutationEnabled?: boolean }): void {
    if (data.spawnInterval !== undefined) {
      this.spawnInterval = data.spawnInterval;
      this.restartSpawnTimer();
    }
    if (data.mutationInterval !== undefined) {
      this.mutationInterval = data.mutationInterval;
      this.restartMutationTimer();
    }
    if (data.mutationEnabled !== undefined) {
      this.mutationEnabled = data.mutationEnabled;
      this.restartMutationTimer();
    }
  }

  private spawnView(ctx: ProcessContext): void {
    this.spawnCounter++;
    const viewId = `${this.id}-view-${this.spawnCounter}`;

    // Get current grid cell size (default to 200 if grid not available)
    const cellSize = state.grid?.cellW ?? 200;

    // Cycle through policy configurations
    const policyIndex = (this.spawnCounter - 1) % POLICY_CONFIGS.length;
    const policyConfig = POLICY_CONFIGS[policyIndex];

    // Randomly pick initial grid size (1x1, 1x2, 2x1, 2x2)
    const gridSizes = [
      { cols: 1, rows: 1 },
      { cols: 1, rows: 2 },
      { cols: 2, rows: 1 },
      { cols: 2, rows: 2 }
    ];
    const initialGridSize = gridSizes[Math.floor(Math.random() * gridSizes.length)];

    // Calculate initial size based on grid size
    const gap = state.grid?.gap ?? 8;
    const initialWidth = initialGridSize.cols * cellSize + (initialGridSize.cols - 1) * gap;
    const initialHeight = initialGridSize.rows * cellSize + (initialGridSize.rows - 1) * gap;

    // Create view with random grid size and selected policy
    const spawnerView = new SpawnerView(this.id, viewId, {
      spawnIndex: this.spawnCounter,
      createdAt: Date.now(),
      priority: policyConfig.priority,
      allowedPages: policyConfig.allowedPages,
      showDismissButton: policyConfig.showDismissButton,
      gridSize: initialGridSize
    }, cellSize, initialWidth, initialHeight, policyConfig);

    const view = spawnerView.toViewInstance();
    this.spawnedViews.set(viewId, spawnerView);

    ctx.registerView(view);
    this.views.set(viewId, view);
  }

  /**
   * Mutate views - AI-driven orchestration
   * Randomly changes sizes and dismisses views to create dynamic interface
   */
  private mutateViews(): void {
    const views = Array.from(this.spawnedViews.values());
    if (views.length === 0) {
      return;
    }

    // Randomly select a view to mutate
    const view = views[Math.floor(Math.random() * views.length)];
    if (!view) return;

    const action = Math.random();
    
    if (action < 0.4) {
      // 40% chance: Resize view
      this.resizeView(view);
    } else if (action < 0.7) {
      // 30% chance: Dismiss view
      this.dismissView(view);
    }
    // 30% chance: Do nothing (natural pause)
  }

  /**
   * Resize a view to a new grid size
   */
  private resizeView(view: SpawnerView): void {
    const gridSizes = [
      { cols: 1, rows: 1 },
      { cols: 1, rows: 2 },
      { cols: 2, rows: 1 },
      { cols: 2, rows: 2 }
    ];

    // Get current size
    const currentSize = view.getGridSize();
    
    // Pick a different size
    const availableSizes = gridSizes.filter(
      size => size.cols !== currentSize.cols || size.rows !== currentSize.rows
    );
    
    if (availableSizes.length > 0) {
      const newSize = availableSizes[Math.floor(Math.random() * availableSizes.length)];
      view.changeGridSize(newSize.cols, newSize.rows);
    }
  }

  /**
   * Dismiss a view
   */
  private dismissView(view: SpawnerView): void {
    // Emit dismiss event
    bus.emit('process.view.dismiss', {
      viewId: view.id,
      processId: this.id
    });

    // Remove from our tracking
    this.spawnedViews.delete(view.id);
    this.views.delete(view.id);
  }

  /**
   * Handle external mutation signals (from AI orchestrator)
   */
  private handleMutationSignal(data: { viewId?: string; action: 'resize' | 'dismiss' | 'random'; gridSize?: { cols: number; rows: number } }): void {
    if (data.action === 'random') {
      this.mutateViews();
      return;
    }

    let view: SpawnerView | undefined;
    
    if (data.viewId) {
      // Specific view
      view = this.spawnedViews.get(data.viewId);
    } else {
      // Random view
      const views = Array.from(this.spawnedViews.values());
      if (views.length > 0) {
        view = views[Math.floor(Math.random() * views.length)];
      }
    }

    if (!view) return;

    if (data.action === 'resize' && data.gridSize) {
      view.changeGridSize(data.gridSize.cols, data.gridSize.rows);
    } else if (data.action === 'dismiss') {
      this.dismissView(view);
    }
  }

  protected onStop(): void {
    // Cleanup is handled by BaseProcess.trackInterval()
    // The interval is automatically cleared when the process stops
    if (this.mutateHandler) {
      bus.off('spawner.mutate', this.mutateHandler);
      this.mutateHandler = null;
    }
    if (this.configHandler) {
      bus.off('spawner.config', this.configHandler);
      this.configHandler = null;
    }
  }
}

/**
 * SpawnerView - extends BaseViewHTML for spawner process
 * 
 * Each view has a dynamic grid preference (1x1, 1x2, 2x1, 2x2) and is color-coded
 * based on its lifecycle and allocation policies.
 */
class SpawnerView extends BaseViewHTML {
  private policyConfig: PolicyConfig;
  private baseCellSize: number;

  constructor(
    processId: string,
    viewId: string,
    private viewState: SpawnerViewState,
    cellSize: number,
    initialWidth: number,
    initialHeight: number,
    policyConfig: PolicyConfig
  ) {
    // Build acceptableSizes from initial grid size
    const acceptableSizes: Array<{ cols: number; rows: number }> = [
      { cols: viewState.gridSize.cols, rows: viewState.gridSize.rows }, // Preferred: initial size
      { cols: 1, rows: 1 } // Minimum: 1x1
    ];

    super(processId, viewId, {
      acceptableSizes,
      showDismissButton: policyConfig.showDismissButton,
      priority: policyConfig.priority,
      allowedPages: policyConfig.allowedPages
    });

    this.policyConfig = policyConfig;
    this.baseCellSize = cellSize;
  }

  /**
   * Change grid size dynamically (1x1, 1x2, 2x1, 2x2)
   */
  changeGridSize(cols: number, rows: number): void {
    if (!state.grid) return;

    const cellW = state.grid.cellW;
    const cellH = state.grid.cellH;
    const gap = state.grid.gap;

    // Calculate new size: width = cols * cellW + (cols - 1) * gap
    const newWidth = cols * cellW + (cols - 1) * gap;
    const newHeight = rows * cellH + (rows - 1) * gap;

    // Update view state
    this.viewState.gridSize = { cols, rows };

    // Update acceptableSizes with new grid size
    if (this.metadata && this.metadata.sizeConstraints) {
      this.metadata.sizeConstraints.acceptableSizes = [
        { cols, rows }, // Preferred: new size
        { cols: 1, rows: 1 } // Minimum: 1x1
      ];
    }

    // Trigger re-allocation
    bus.emit('view.needs-allocation', {
      viewId: this.id
    });

    // Trigger canvas re-render
    this.markDirty();
  }

  /**
   * Get current grid size
   */
  getGridSize(): { cols: number; rows: number } {
    return this.viewState.gridSize;
  }

  /**
   * Check if view can be unmounted
   */

  protected getBackgroundStyle(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ): string | CanvasGradient | CanvasPattern {
    // Return default subtle gradient - use subtle elements in render() instead of bold background
    // Use the base class default gradient for consistency
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0.20)');
    gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.16)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0.12)');
    return gradient;
  }

  protected getContentHTML(): string {
    const age = Math.floor((Date.now() - this.viewState.createdAt) / 1000);
    const flags: string[] = [];
    if (this.viewState.priority && this.viewState.priority > 1) flags.push(`PRIORITY:${this.viewState.priority}`);
    if (this.viewState.allowedPages && this.viewState.allowedPages.length > 0) flags.push(`PAGES:${this.viewState.allowedPages.join(',')}`);
    if (!this.viewState.showDismissButton) flags.push('NO-DISMISS');
    
    return `
      <div style="
        padding: 8px;
        height: 100%;
        display: flex;
        flex-direction: column;
        position: relative;
      ">
        <div style="
          position: absolute;
          top: 8px;
          left: 8px;
          width: 8px;
          height: 8px;
          background: ${this.policyConfig.backgroundColor};
          opacity: 0.3;
          border-radius: 50%;
        "></div>
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: ${this.policyConfig.borderColor};
          opacity: 0.2;
        "></div>
        <div style="
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
        ">
          <div style="
            font-size: 11px;
            font-weight: bold;
            color: var(--ink, #cfd3dc);
          ">
            #${this.viewState.spawnIndex}
          </div>
          <div style="
            font-size: 8px;
            color: rgba(107, 114, 128, 0.5);
          ">
            ${age}s
          </div>
        </div>
        <div style="
          font-size: 9px;
          font-weight: bold;
          color: ${this.policyConfig.borderColor};
          margin-bottom: 4px;
        ">
          ${this.escapeHtml(this.policyConfig.label)}
        </div>
        <div style="
          font-size: 8px;
          color: rgba(207, 211, 220, 0.7);
          margin-bottom: 8px;
          line-height: 1.3;
        ">
          ${this.escapeHtml(this.policyConfig.description)}
        </div>
        ${flags.length > 0 ? `
          <div style="
            font-size: 7px;
            color: rgba(107, 114, 128, 0.4);
            margin-bottom: auto;
            display: flex;
            flex-direction: column;
            gap: 2px;
          ">
            ${flags.map(flag => `<div>${this.escapeHtml(flag)}</div>`).join('')}
          </div>
        ` : '<div style="flex: 1;"></div>'}
        <div style="
          margin-top: auto;
          padding: 4px;
          background: rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(0, 0, 0, 0.2);
          border-radius: 2px;
          font-size: 8px;
          color: var(--ink, #cfd3dc);
        ">
          ${this.viewState.gridSize.cols}×${this.viewState.gridSize.rows}
        </div>
      </div>
    `;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  protected onDestroy(): void {
    // Cleanup handled by process
  }
}

registerProcess('spawner', (id: string) => new SpawnerProcess(id));

